import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api, prepareReceipt } from '../lib/api.js'
import { aud } from '../lib/format.js'
import { SITE } from '../data/site.js'
import Icon from '../components/Icon.jsx'

const STATUS_INFO = {
  awaiting_payment: { label: 'Awaiting payment', tone: 'warn', text: `Transfer the total using the reference below, then tap "I've made the transfer".` },
  payment_review: { label: 'Payment being verified', tone: 'info', text: `Thanks — we're checking your transfer. You'll hear from us shortly.` },
  paid: { label: 'Payment received', tone: 'ok', text: `Payment confirmed! We're preparing your order now.` },
  fulfilled: { label: 'Completed', tone: 'ok', text: `Your order has been picked up / delivered. Thanks for shopping with us!` },
  cancelled: { label: 'Cancelled', tone: 'danger', text: `This order was cancelled. Contact us if that doesn't look right.` },
}

export default function Order() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const token = params.get('t') || ''
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [reporting, setReporting] = useState(false)
  const [paying, setPaying] = useState(false)
  const [payError, setPayError] = useState('')
  const [copied, setCopied] = useState('')
  const [receipt, setReceipt] = useState(null)        // compressed File ready to upload
  const [receiptPreview, setReceiptPreview] = useState('')
  const [receiptError, setReceiptError] = useState('')

  const load = () => api.order(id, token).then(setData).catch((e) => setError(e.message))
  useEffect(() => { load() }, [id, token])

  async function chooseReceipt(e) {
    const file = e.target.files?.[0]
    e.target.value = '' // let the same file be picked again after a remove
    if (!file) return
    setReceiptError('')
    try {
      const prepared = await prepareReceipt(file)
      if (receiptPreview) URL.revokeObjectURL(receiptPreview)
      setReceipt(prepared)
      setReceiptPreview(prepared.type === 'application/pdf' ? '' : URL.createObjectURL(prepared))
    } catch (err) {
      setReceipt(null)
      setReceiptPreview('')
      setReceiptError(err.message)
    }
  }

  function removeReceipt() {
    if (receiptPreview) URL.revokeObjectURL(receiptPreview)
    setReceipt(null)
    setReceiptPreview('')
    setReceiptError('')
  }

  async function reportPayment() {
    setReporting(true)
    setReceiptError('')
    try {
      await api.reportPayment(id, token, receipt)
      removeReceipt()
      await load()
    } catch (e) {
      // A failed slip upload must not look like a lost order — keep the page and say why.
      setReceiptError(e.message)
    } finally {
      setReporting(false)
    }
  }

  async function payByCard() {
    setPaying(true)
    setPayError('')
    try {
      const { url } = await api.stripeSession(id, token)
      window.location.href = url
    } catch (e) {
      // Keep the order on screen — a card failure must not look like a lost order.
      setPayError(e.message)
      setPaying(false)
    }
  }

  function copy(text, key) {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key)
      setTimeout(() => setCopied(''), 1500)
    })
  }

  if (error) {
    return (
      <main className="container page">
        <p className="muted">We couldn't find that order. {error}</p>
        <Link to="/" className="btn btn-primary">Back to home</Link>
      </main>
    )
  }
  if (!data) return <main className="container page"><p className="muted">Loading order…</p></main>

  const { order, bank } = data
  const items = JSON.parse(order.items)
  const isCash = order.payment_method === 'cash'
  const unpaid = order.status === 'awaiting_payment' || order.status === 'payment_review'
  const info = isCash && unpaid
    ? { label: 'Reserved for pickup', tone: 'info', text: `Nothing to pay online — bring cash when you collect.` }
    : STATUS_INFO[order.status] || STATUS_INFO.awaiting_payment
  // Bank details and the "I've made the transfer" button make no sense for cash: there
  // is no transfer to report, the money changes hands over the counter.
  const showBank = unpaid && !isCash
  // Set by checkout when card was chosen but Stripe wouldn't open a session.
  const cardFailed = params.get('cardfail') === '1' && order.payment_method === 'bank'
  // A slip can still be added after reporting the transfer — customers often forget it.
  const canAttach = unpaid && !isCash

  return (
    <main className="container page order-page">
      <header className="page-head">
        <h1>Thanks, {order.name.split(' ')[0]}! Your order is in.</h1>
        <p>Order <strong>{order.payment_reference}</strong> · placed {new Date(order.created).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
      </header>

      <div className={`status-banner status-${info.tone}`}>
        <strong>{info.label}</strong>
        <span>{info.text}</span>
      </div>

      <div className="order-layout">
        {isCash && unpaid && (
          <section className="panel cash-panel">
            <h2><Icon name="pin" size={20} /> Collect and pay at our warehouse</h2>
            <p className="cash-deadline">
              Please collect by <strong>{order.pickup_by?.label || 'the agreed date'}</strong>
            </p>
            <p className="muted">
              We're holding this order for you. Bring <strong>{aud(order.total)}</strong> in cash and quote
              order <strong>{order.payment_reference}</strong> at the counter.
            </p>
            <dl className="bank-details">
              <div><dt>Address</dt><dd><a href={SITE.mapsUrl} target="_blank" rel="noreferrer">{SITE.address}</a></dd></div>
              <div><dt>Open</dt><dd>{SITE.hours}</dd></div>
              <div><dt>Phone</dt><dd><a href={SITE.phoneHref}>{SITE.phone}</a></dd></div>
              <div><dt>Amount</dt><dd><strong>{aud(order.total)}</strong> inc GST</dd></div>
            </dl>
            <p className="muted cash-late">
              Can't make it in time? Call us on <a href={SITE.phoneHref}>{SITE.phone}</a> and we'll hold it longer —
              otherwise the stock may go back on sale.
            </p>
          </section>
        )}
        {showBank && (
          <section className="panel bank-panel">
            {cardFailed && order.status === 'awaiting_payment' && (
              <div className="card-retry">
                <h2><Icon name="shield" size={20} /> Card payment unavailable</h2>
                <p className="muted">
                  We couldn't open the secure card checkout for this order. You can try again,
                  or pay by bank transfer below — your order is saved either way.
                </p>
                <button className="btn btn-primary btn-lg btn-block" onClick={payByCard} disabled={paying}>
                  {paying ? 'Opening secure checkout…' : `Try card again — ${aud(order.total)}`}
                </button>
                {payError && <p className="field-error">{payError}</p>}
                <div className="or-divider"><span>or</span></div>
              </div>
            )}
            {order.payment_method === 'card' && order.status === 'awaiting_payment' && (
              <div className="card-retry">
                <h2><Icon name="shield" size={20} /> Card payment not completed</h2>
                <p className="muted">Your card payment didn't go through. You can try again, or pay by bank transfer below.</p>
                <button className="btn btn-primary btn-lg btn-block" onClick={payByCard} disabled={paying}>
                  {paying ? 'Opening secure checkout…' : `Pay by card — ${aud(order.total)}`}
                </button>
                {payError && <p className="field-error">{payError}</p>}
                <div className="or-divider"><span>or</span></div>
              </div>
            )}
            <h2><Icon name="bank" size={20} /> Pay by bank transfer</h2>
            {bank.bank_account_number ? (
              <>
                <dl className="bank-details">
                  <div><dt>Account name</dt><dd>{bank.bank_account_name}</dd></div>
                  {bank.bank_name && <div><dt>Bank</dt><dd>{bank.bank_name}</dd></div>}
                  <div>
                    <dt>BSB</dt>
                    <dd>{bank.bank_bsb} <button className="copy-btn" onClick={() => copy(bank.bank_bsb, 'bsb')}>{copied === 'bsb' ? 'Copied!' : 'Copy'}</button></dd>
                  </div>
                  <div>
                    <dt>Account number</dt>
                    <dd>{bank.bank_account_number} <button className="copy-btn" onClick={() => copy(bank.bank_account_number, 'acc')}>{copied === 'acc' ? 'Copied!' : 'Copy'}</button></dd>
                  </div>
                  {bank.payid && (
                    <div>
                      <dt>PayID</dt>
                      <dd>{bank.payid} <button className="copy-btn" onClick={() => copy(bank.payid, 'payid')}>{copied === 'payid' ? 'Copied!' : 'Copy'}</button></dd>
                    </div>
                  )}
                  <div className="highlight">
                    <dt>Reference (important)</dt>
                    <dd>{order.payment_reference} <button className="copy-btn" onClick={() => copy(order.payment_reference, 'ref')}>{copied === 'ref' ? 'Copied!' : 'Copy'}</button></dd>
                  </div>
                  <div className="highlight">
                    <dt>Amount</dt>
                    <dd>{aud(order.total)} <button className="copy-btn" onClick={() => copy(String(order.total), 'amt')}>{copied === 'amt' ? 'Copied!' : 'Copy'}</button></dd>
                  </div>
                </dl>
                <p className="summary-note">Use the reference exactly so we can match your payment quickly.</p>
              </>
            ) : (
              <p>
                Please contact us to arrange payment: <a href={SITE.phoneHref}>{SITE.phone}</a> or{' '}
                <a href={`mailto:${SITE.email}`}>{SITE.email}</a> and quote reference <strong>{order.payment_reference}</strong>.
              </p>
            )}

            {canAttach && (
              <div className="receipt-attach">
                {order.receipt_type ? (
                  <p className="receipt-done"><Icon name="check" size={18} /> Receipt received — thanks! We'll confirm your order shortly.</p>
                ) : (
                  <>
                    <div className="receipt-attach-head">
                      <Icon name="bank" size={18} />
                      <div>
                        <strong>Attach your transfer receipt (optional)</strong>
                        <small>A screenshot from your banking app helps us confirm your order faster.</small>
                      </div>
                    </div>
                    {receipt ? (
                      <div className="receipt-file">
                        {receiptPreview
                          ? <img src={receiptPreview} alt="Transfer receipt preview" />
                          : <span className="receipt-pdf-chip">PDF</span>}
                        <span className="receipt-file-name">
                          Ready to send
                          <small>{Math.max(1, Math.round(receipt.size / 1024))} KB</small>
                        </span>
                        <button type="button" className="btn btn-outline btn-sm" onClick={removeReceipt}>Remove</button>
                      </div>
                    ) : (
                      <label className="receipt-dropzone">
                        <input type="file" accept="image/*,.pdf" onChange={chooseReceipt} hidden />
                        + Add screenshot or photo
                      </label>
                    )}
                  </>
                )}
                {receiptError && <p className="field-error">{receiptError}</p>}
              </div>
            )}

            {canAttach && !(order.status === 'payment_review' && order.receipt_type) && (
              <button className="btn btn-primary btn-lg btn-block" onClick={reportPayment} disabled={reporting}>
                {reporting
                  ? (receipt ? 'Sending receipt…' : 'Sending…')
                  : order.status === 'payment_review'
                    ? (receipt ? 'Send receipt' : "I've made the transfer")
                    : `I've made the transfer${receipt ? ' — send receipt' : ''}`}
              </button>
            )}
          </section>
        )}

        <section className="panel">
          <h2>Order details</h2>
          <div className="review-items">
            {items.map((it, i) => (
              <div key={i} className="review-item">
                <span>
                  {it.qty} × {it.name}
                  {Object.keys(it.opts || {}).length > 0 && (
                    <small className="muted"> — {Object.values(it.opts).join(', ')}</small>
                  )}
                </span>
                <strong>{aud(it.unitPrice * it.qty)}</strong>
              </div>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal (ex GST)</dt><dd>{aud(order.subtotal)}</dd></div>
            <div><dt>Shipping</dt><dd>{order.shipping === 0 ? 'Free — pickup' : aud(order.shipping)}</dd></div>
            <div><dt>GST (10%)</dt><dd>{aud(order.gst)}</dd></div>
            <div className="total"><dt>Total (inc GST)</dt><dd>{aud(order.total)}</dd></div>
          </dl>
          <h3>{order.delivery === 'pickup' ? 'Pickup' : 'Delivery'}</h3>
          <p className="muted">
            {order.delivery === 'pickup'
              ? `${SITE.address} — we'll contact you on ${order.phone} when it's ready.`
              : `${order.address}, ${order.suburb} ${order.state} ${order.postcode}`}
          </p>
          <p className="summary-note">Save this page's link to check your order status anytime.</p>
        </section>
      </div>
    </main>
  )
}
